import { useCallback, useEffect, useReducer } from 'react'

import { Box, Drawer, IconButton, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Close as CloseIcon } from '@material-ui/icons'
import { AppMethodDisplay, ApplicantType, ApplicationStatusType } from '@scoir/application-info'
import { format } from 'date-fns'

import api from 'common/api'
import { CollegeDocumentType } from 'common/document.labels.type'
import useQuery from 'common/use-query-params'
import { useHistory } from 'components/ReactRouterProvider/context'
import { useAppSelector } from 'store'
import { hasPermission } from 'store/user'
import { useScid } from 'store/user/hooks'

import BatchedDocumentsTable, { BatchedDocument } from './BatchedDocumentsTable'
import DownloadScoirApplyProfile from './DownloadScoirApplyProfile'

const useStyles = makeStyles((theme) => ({
  applicantInfoTable: {
    '& tr': {},
    '& th': {
      paddingRight: theme.spacing(2),
      paddingBottom: theme.spacing(1),
      fontSize: theme.typography.pxToRem(14),
    },
    '& td': {
      fontSize: theme.typography.pxToRem(14),
      paddingBottom: theme.spacing(1),
    },
  },
  drawerBody: {
    width: 320,
    [theme.breakpoints.up('sm')]: {
      width: 600,
    },
    [theme.breakpoints.up('xl')]: {
      width: 1000,
    },
  },
  closeIcon: {
    color: 'white',
  },
  sectionHeader: {
    fontSize: theme.typography.pxToRem(20),
    marginBottom: theme.spacing(3),
  },
  drawerHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 2),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: 'space-between',
    backgroundColor: theme.palette.primary.main,
  },
}))

export const useApplicantId = (): [string | undefined, (id?: string) => void] => {
  const history = useHistory()
  const params = useQuery()
  const applicantId = params.get('applicantId')
  const selectApplicant = useCallback(
    (id?: string): void => {
      params.delete('applicantId')
      if (id) {
        params.append('applicantId', id)
      }
      history.replace({ search: params.toString() })
    },
    [params],
  )
  return [applicantId ?? undefined, selectApplicant]
}

type BatchedDocumentAPI = {
  Type: CollegeDocumentType
  BatchDate: string
  Id: string
  BatchId: string
}
type ferpaStatusType = 'Unsigned' | 'Waived' | 'Granted' | 'Withheld'

type apiApplicantPayload = {
  Id: string
  FirstName: string
  LastName: string
  HighSchoolName: string
  Birthdate: string
  AppMethod: MethodTypes
  City: string
  State: string
  Country: string
  FERPAStatus: ferpaStatusType
  ApplicantType: ApplicantType
  ApplicationStatus: ApplicationStatusType
  ApplicationId: string
  DidNotAttendHighSchool?: boolean
  ErrorGettingDocuments?: boolean
  IsFirstGen: boolean
  HasFeeWaiver: boolean
  CollegeScid: number
  Type: string // Interest | Following included
  Documents: BatchedDocumentAPI[]
}
type drawerApplicant = {
  id: string
  firstName: string
  lastName: string
  highSchoolName: string
  birthdate: string
  appMethod: MethodTypes
  applicantType: ApplicantType
  applicationStatus: ApplicationStatusType
  applicationId: string
  collegeScid: number
  city: string
  state: string
  country: string
  ferpaStatus: ferpaStatusType
  didNotAttendHighSchool?: boolean
  errorGettingDocuments?: boolean
  isFirstGen: boolean
  hasFeeWaiver: boolean
  type: string // Interest | Following included
  documents: BatchedDocument[]
}
type actionType =
  | {
      type: 'loading'
    }
  | {
      type: 'clear'
    }
  | {
      type: 'failure'
      payload: string
    }
  | {
      type: 'dataRetrieved'
      payload: apiApplicantPayload
    }

type stateType = {
  errorId?: string
  isLoading?: boolean
  selectedApplicant?: drawerApplicant
}

const reducer = (state: stateType, action: actionType): stateType => {
  switch (action.type) {
    case 'loading': {
      return {
        ...state,
        isLoading: true,
        errorId: '',
      }
    }
    case 'failure': {
      return {
        ...state,
        isLoading: false,
        errorId: action.payload,
      }
    }
    case 'dataRetrieved': {
      const initialDate = new Date(action.payload.Birthdate)
      const date = initialDate.getUTCDate()
      const month = initialDate.getUTCMonth()
      const year = initialDate.getUTCFullYear()
      const localDateFromUTCDate = new Date(year, month, date)
      return {
        ...state,
        isLoading: false,
        selectedApplicant: {
          id: action.payload.Id,
          firstName: action.payload.FirstName,
          lastName: action.payload.LastName,
          applicationId: action.payload.ApplicationId,
          highSchoolName: action.payload.HighSchoolName,
          appMethod: action.payload.AppMethod,
          applicantType: action.payload.ApplicantType,
          applicationStatus: action.payload.ApplicationStatus,
          city: action.payload.City,
          state: action.payload.State,
          country: action.payload.Country,
          ferpaStatus: action.payload.FERPAStatus,
          isFirstGen: action.payload.IsFirstGen,
          birthdate: format(localDateFromUTCDate, 'MM/dd/yyyy'),
          didNotAttendHighSchool: action.payload.DidNotAttendHighSchool,
          errorGettingDocuments: action.payload.ErrorGettingDocuments,
          hasFeeWaiver: action.payload.HasFeeWaiver,
          type: action.payload.Type,
          collegeScid: action.payload.CollegeScid,
          documents: action.payload.Documents.map((doc) => ({
            type: doc.Type,
            batchDate: format(new Date(doc.BatchDate), 'MM/dd/yyyy'),
            id: doc.Id,
            batchId: doc.BatchId,
          })).filter((doc) => doc.type !== 'ApplicationProfile'),
        },
      }
    }
    case 'clear': {
      return {
        ...state,
        selectedApplicant: undefined,
      }
    }
  }
}
type MethodTypes = 'CommonApp' | 'Coalition' | 'ScoirApply' | 'CollegeWebsite' | 'OtherApp'

const FocusedApplicantDrawer = () => {
  const classes = useStyles()
  const [applicantId, selectApplicant] = useApplicantId()

  const [{ isLoading, selectedApplicant, errorId }, dispatch] = useReducer(reducer, {})
  const canViewDocuments = useAppSelector((state) =>
    hasPermission(state, 'PERFORM_ADMISSIONS_TASKS_FOR_ASSOCIATED_COLLEGE'),
  )
  const scid = useScid()
  useEffect(() => {
    // TODO: REMOVE errorId check here
    if (errorId !== applicantId && !isLoading && applicantId && applicantId !== selectedApplicant?.id) {
      dispatch({ type: 'loading' })
      api
        .get<apiApplicantPayload>(`/api/colleges/${scid}/applicants/${applicantId}`)
        .then((payload) => dispatch({ type: 'dataRetrieved', payload }))
        .catch(() => {
          dispatch({ type: 'failure', payload: applicantId })
        })
    } else if (selectedApplicant && !applicantId) {
      dispatch({ type: 'clear' })
    }
  }, [isLoading, applicantId, selectedApplicant?.id, scid])
  const ferpa = selectedApplicant?.ferpaStatus
    ? selectedApplicant?.ferpaStatus === 'Granted'
      ? 'Waived'
      : selectedApplicant?.ferpaStatus
    : 'Unsigned'
  const countryIsUnitedStates = selectedApplicant?.country === 'United States'

  return (
    <Drawer
      open={!!applicantId}
      variant="temporary"
      PaperProps={{ elevation: 16 }}
      onClose={() => selectApplicant()}
      anchor="right"
    >
      <Box className={classes.drawerHeader}>
        <DrawerTrigger
          applicantId=""
          ariaLabel="close applicant focus"
        >
          <CloseIcon className={classes.closeIcon} />
        </DrawerTrigger>
      </Box>
      <Box
        className={classes.drawerBody}
        p={3}
      >
        {errorId && (
          <Box
            mt={1}
            textAlign="center"
          >
            <Typography
              color="error"
              role="alert"
            >
              Unable to load applicant information
            </Typography>
          </Box>
        )}
        {selectedApplicant && (
          <Box>
            <Typography
              className={classes.sectionHeader}
              variant="h6"
            >
              Applicant Details
            </Typography>
            <Box mb={3}>
              <AppMethodDisplay
                appMethod={selectedApplicant.appMethod}
                type={selectedApplicant.type}
                applicantType={selectedApplicant.applicantType}
                applicationStatus={selectedApplicant.applicationStatus}
              />
            </Box>
            {selectedApplicant.appMethod === 'ScoirApply' && (
              <Box my={3}>
                <DownloadScoirApplyProfile
                  scid={scid}
                  isPermitted={canViewDocuments}
                  applicationId={selectedApplicant.applicationId}
                />
              </Box>
            )}
            <Typography
              className={classes.sectionHeader}
              variant="h6"
            >
              Applicant Info
            </Typography>
            <Box mb={3}>
              <table
                aria-label="Applicant Information Table"
                className={classes.applicantInfoTable}
              >
                <tbody>
                  <tr>
                    <th scope="row">Name</th>
                    <td>
                      {selectedApplicant.firstName} {selectedApplicant.lastName}
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">DOB</th>
                    <td>{selectedApplicant.birthdate}</td>
                  </tr>
                  <tr>
                    <th scope="row">High School</th>
                    <td>
                      {selectedApplicant.highSchoolName && !selectedApplicant.didNotAttendHighSchool
                        ? selectedApplicant.highSchoolName
                        : 'Did Not Attend'}
                    </td>
                  </tr>
                  {selectedApplicant.highSchoolName && !selectedApplicant.didNotAttendHighSchool && (
                    <tr>
                      <th scope="row">Location</th>
                      <td>
                        {selectedApplicant.city}
                        {selectedApplicant.city && selectedApplicant.state ? ', ' : ''}
                        {selectedApplicant.state}
                        {selectedApplicant.state && selectedApplicant.country && !countryIsUnitedStates ? ', ' : ''}
                        {countryIsUnitedStates ? '' : selectedApplicant.country}
                      </td>
                    </tr>
                  )}
                  <tr>
                    <th scope="row">FERPA</th>
                    <td>{ferpa}</td>
                  </tr>
                  {selectedApplicant.appMethod === 'ScoirApply' && (
                    <>
                      <tr>
                        <th scope="row">Fee Waiver</th>
                        <td>{selectedApplicant.hasFeeWaiver ? 'Yes' : 'No'}</td>
                      </tr>
                      <tr>
                        <th scope="row">First Gen</th>
                        <td>{selectedApplicant.isFirstGen ? 'Yes' : 'No'}</td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </Box>
            <Box>
              <Typography
                className={classes.sectionHeader}
                variant="h6"
              >
                Docs
              </Typography>
              <BatchedDocumentsTable
                collegeScid={selectedApplicant.collegeScid}
                isPermitted={canViewDocuments}
                data={selectedApplicant.documents}
                studentFirstName={selectedApplicant.firstName}
                studentLastName={selectedApplicant.lastName}
              />
              {selectedApplicant.errorGettingDocuments && (
                <Box
                  mt={1}
                  textAlign="center"
                >
                  <Typography
                    color="error"
                    role="alert"
                    align="left"
                  >
                    There was a problem loading docs. Please try again or contact Support for additional assistance.
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Drawer>
  )
}

export default FocusedApplicantDrawer

export const DrawerTrigger = ({
  applicantId,
  ariaLabel,
  ...rest
}: {
  children: React.ReactNode
  applicantId?: string
  ariaLabel: string
}) => {
  const [, selectApplicantId] = useApplicantId()
  return (
    <IconButton
      {...rest}
      color="primary"
      aria-label={ariaLabel}
      onClick={() => selectApplicantId(applicantId)}
    />
  )
}
